import React, { useState, useEffect } from 'react';
import { Spin, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GOLD = '#B8892A';
const BD = '#E8E0D0';
const BG = '#FAFAF8';
const MUTED = '#6B5E4E';
const MUTED_LIGHT = '#A8998A';

// DB 枚举 = pending / verified / rejected / expired
// under_review 是前端派生：status='pending' 但 kyc_submitted_at 不为空（已提交等审核）
const KYC_STATUS_BASE = {
  pending:      { labelKey: 'profile.kycNotSubmitted',  color: MUTED_LIGHT, bg: '#F2EFE9',              border: BD },
  under_review: { labelKey: 'profile.kycPending',  color: '#8C5A0A',   bg: 'rgba(140,90,10,0.08)', border: 'rgba(140,90,10,0.2)' },
  verified:     { labelKey: 'profile.kycVerified',  color: '#2A6645',   bg: 'rgba(42,102,69,0.08)', border: 'rgba(42,102,69,0.2)' },
  rejected:     { labelKey: 'profile.kycRejected',  color: '#9B2335',   bg: 'rgba(155,35,53,0.08)', border: 'rgba(155,35,53,0.2)' },
  expired:      { labelKey: 'profile.kycRejected',  color: '#8C5A0A',   bg: 'rgba(140,90,10,0.08)', border: 'rgba(140,90,10,0.2)' },
};
function deriveKycKey(data) {
  if (data?.kyc_status === 'pending' && data?.kyc_submitted_at) return 'under_review';
  return data?.kyc_status || 'pending';
}

const TIER_CONFIG_BASE = {
  basic:    { labelKey: 'membership.bronze', color: MUTED_LIGHT, next: 'VIP', target: 100000 },
  verified: { labelKey: 'membership.silver', color: '#1A4F8C', next: 'VIP', target: 100000 },
  vip:      { labelKey: 'membership.gold',   color: GOLD, next: 'VIP+', target: 500000 },
};

const MENU_ITEMS_BASE = [
  { key: 'overview', icon: '📊', labelKey: 'profile.overview' },
  { key: 'kyc', icon: '🪪', labelKey: 'profile.kyc' },
  { key: 'password', icon: '🔒', labelKey: 'settings.changePassword' },
];

function inputStyle(focused) {
  return {
    width: '100%', padding: '11px 14px',
    border: '1px solid ' + (focused ? GOLD : BD),
    borderRadius: 3, fontFamily: 'var(--mono)', fontSize: 14, color: '#110E0A',
    background: BG, outline: 'none',
    boxShadow: focused ? '0 0 0 3px rgba(184,137,42,0.07)' : 'none',
  };
}

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user: authUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // 支持从用户下拉菜单跳过来时直接定位到指定 tab
  //   navigate('/profile', { state: { section: 'kyc' } })
  // 已在 /profile 时再次点击下拉也要切 tab —— 用 useEffect 监听 location.state
  const [activeSection, setActiveSection] = useState(location.state?.section || 'overview');
  useEffect(() => {
    const target = location.state?.section;
    if (target) setActiveSection(target);
  }, [location.state, location.key]);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [realName, setRealName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  // KYC photos
  const [kycPhotos, setKycPhotos] = useState({ id_front: null, id_back: null, selfie: null });
  const [kycUploading, setKycUploading] = useState({ id_front: false, id_back: false, selfie: false });
  const [focusedField, setFocusedField] = useState(null);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customers/me');
      setProfile(data);
    } catch (err) {
      message.error(t('common.failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handlePhotoUpload = async (type, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) { message.error(t('profile.photoFormatError')); return; }
    if (file.size > 10 * 1024 * 1024) { message.error(t('profile.photoSizeError')); return; }
    setKycUploading(p => ({ ...p, [type]: true }));
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await api.post(`/customers/kyc/photo-upload?type=${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setKycPhotos(p => ({ ...p, [type]: res.data.url }));
      message.success(t('profile.photoUploadSuccess'));
    } catch (err) {
      message.error(err.response?.data?.error || t('profile.uploadFailed'));
    } finally {
      setKycUploading(p => ({ ...p, [type]: false }));
    }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    if (!realName.trim()) { message.error(t('profile.enterRealName')); return; }
    if (!/^\d{17}[\dXx]$/.test(idNumber)) { message.error(t('profile.enterIdNumber')); return; }
    if (!kycPhotos.id_front) { message.error(t('profile.uploadIdFront')); return; }
    if (!kycPhotos.id_back)  { message.error(t('profile.uploadIdBack')); return; }
    if (!kycPhotos.selfie)   { message.error(t('profile.uploadSelfie')); return; }
    setKycSubmitting(true);
    try {
      await api.post('/customers/kyc', {
        real_name: realName, id_number: idNumber,
        kyc_id_front_url: kycPhotos.id_front,
        kyc_id_back_url:  kycPhotos.id_back,
        kyc_selfie_url:   kycPhotos.selfie,
      });
      message.success(t('profile.kycSubmitSuccess'));
      fetchProfile(); refreshUser();
      setRealName(''); setIdNumber('');
      setKycPhotos({ id_front: null, id_back: null, selfie: null });
    } catch (err) {
      message.error(err.response?.data?.message || t('profile.submitFailed'));
    } finally {
      setKycSubmitting(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdMsg(null);
    if (!currentPwd) { setPwdMsg({ type: 'err', text: t('profile.enterCurrentPassword') }); return; }
    if (newPwd.length < 8) { setPwdMsg({ type: 'err', text: t('profile.passwordMinLength') }); return; }
    if (!/^(?=.*[a-zA-Z])(?=.*\d)/.test(newPwd)) { setPwdMsg({ type: 'err', text: t('profile.passwordMustContainLetterAndNumber') }); return; }
    if (newPwd !== confirmPwd) { setPwdMsg({ type: 'err', text: t('profile.passwordMismatch') }); return; }
    setPwdSubmitting(true);
    try {
      await api.put('/customers/password', { current_password: currentPwd, new_password: newPwd });
      setPwdMsg({ type: 'ok', text: t('settings.passwordChanged') });
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      setPwdMsg({ type: 'err', text: err.response?.data?.message || t('common.failed') });
    } finally {
      setPwdSubmitting(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;

  const data = profile || authUser || {};
  const kycKey = deriveKycKey(data);            // 派生：pending/under_review/verified/rejected/expired
  const isKycVerified = kycKey === 'verified';
  const kycBase = KYC_STATUS_BASE[kycKey] || KYC_STATUS_BASE.pending;
  const kycCfg = { ...kycBase, label: t(kycBase.labelKey) };
  const tierBase = TIER_CONFIG_BASE[data.tier] || TIER_CONFIG_BASE.basic;
  const tierCfg = { ...tierBase, label: t(tierBase.labelKey) };
  const MENU_ITEMS = MENU_ITEMS_BASE.map(m => ({ ...m, label: t(m.labelKey) }));
  // 已认证用户不再显示 KYC 菜单项
  const visibleMenu = MENU_ITEMS.filter(m => m.key !== 'kyc' || !isKycVerified);
  const nameInitial = (data.name || data.real_name || '用').charAt(0);
  const totalAmount = Number(data.total_amount || 0);
  const progress = tierCfg.target > 0 ? Math.min(100, Math.round((totalAmount / tierCfg.target) * 100)) : 0;

  return (
    <div style={{ background: BG, minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '16px' : '32px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: isMobile ? 16 : 24 }}>
        {/* ── Left Sidebar ── */}
        <div>
          {/* Profile card */}
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '28px 20px', textAlign: 'center' }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%', margin: '0 auto 12px',
                background: 'linear-gradient(135deg, rgba(184,137,42,0.12), rgba(184,137,42,0.05))',
                border: '2px solid rgba(184,137,42,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--serif)', fontSize: 36, color: GOLD,
                position: 'relative',
              }}>
                {nameInitial}
                <div style={{
                  position: 'absolute', bottom: -2, right: -2,
                  background: tierCfg.color, color: '#fff',
                  fontFamily: 'var(--mono)', fontSize: 8, fontWeight: 600, letterSpacing: 1,
                  padding: '2px 6px', borderRadius: 4,
                }}>
                  {tierCfg.label}
                </div>
              </div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 20, color: '#110E0A', marginBottom: 4 }}>
                {data.name || data.real_name || t('profile.defaultUser')}
              </div>
              {data.customer_no && (
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT, marginBottom: 4 }}>
                  会员编号 {String(data.customer_no).slice(-4)}
                </div>
              )}
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: MUTED_LIGHT, marginBottom: 8 }}>
                📱 {data.phone || '—'}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontFamily: 'var(--mono)', fontSize: 9, padding: '2px 10px', borderRadius: 2,
                background: kycCfg.bg, border: '1px solid ' + kycCfg.border, color: kycCfg.color,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: kycCfg.color }} />
                KYC {kycCfg.label}
              </div>
            </div>

            {/* Deposit mini card */}
            <div style={{ margin: '0 16px 16px', padding: '14px', background: '#1A1410', borderRadius: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.5)' }}>{t('deposit.balance')}</span>
                <button onClick={() => navigate('/deposit')} style={{ padding: '3px 10px', borderRadius: 3, border: '1px solid rgba(184,137,42,0.4)', background: 'transparent', color: GOLD, fontSize: 10, cursor: 'pointer' }}>{t('deposit.recharge')}</button>
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 20, color: GOLD, fontWeight: 600 }}>
                ¥{Number(data.available_cny || 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Menu */}
          <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, overflow: isMobile ? 'visible' : 'hidden' }}>
          {isMobile ? (
            <div style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', borderBottom: '1px solid ' + BD }}>
              {visibleMenu.map(item => (
                <button key={item.key} onClick={() => setActiveSection(item.key)} style={{
                  flexShrink: 0, padding: '10px 14px', background: 'transparent', border: 'none',
                  borderBottom: `2px solid ${activeSection === item.key ? GOLD : 'transparent'}`,
                  cursor: 'pointer', fontSize: 12, color: activeSection === item.key ? GOLD : MUTED,
                  fontFamily: 'var(--sans)', whiteSpace: 'nowrap', transition: 'all 0.13s',
                }}>
                  {item.icon} {item.label}
                </button>
              ))}
            </div>
          ) : (
            <>
            {visibleMenu.map(item => (
              <div key={item.key} onClick={() => setActiveSection(item.key)} style={{
                padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                borderLeft: `3px solid ${activeSection === item.key ? GOLD : 'transparent'}`,
                background: activeSection === item.key ? 'rgba(184,137,42,0.06)' : 'transparent',
                color: activeSection === item.key ? GOLD : MUTED,
                fontSize: 13, transition: 'all 0.13s',
                borderBottom: '1px solid ' + BD,
              }}>
                <span style={{ fontSize: 14 }}>{item.icon}</span> {item.label}
              </div>
            ))}
            <div onClick={() => navigate('/mandates')} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: MUTED, borderBottom: '1px solid ' + BD }}>
              <span style={{ fontSize: 14 }}>📋</span> {t('mandates.title')}
            </div>
            <div onClick={() => navigate('/orders')} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: MUTED, borderBottom: '1px solid ' + BD }}>
              <span style={{ fontSize: 14 }}>📦</span> {t('orders.title')}
            </div>
            <div onClick={() => navigate('/bills')} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: MUTED, borderBottom: '1px solid ' + BD }}>
              <span style={{ fontSize: 14 }}>🧾</span> {t('bill.title')}
            </div>
            <div onClick={() => navigate('/deposit')} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: MUTED, borderBottom: '1px solid ' + BD }}>
              <span style={{ fontSize: 14 }}>💴</span> {t('deposit.detail')}
            </div>
            <div onClick={() => navigate('/settings')} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: MUTED }}>
              <span style={{ fontSize: 14 }}>⚙</span> {t('settings.title')}
            </div>
            </>
          )}
          </div>
        </div>

        {/* ── Right Content ── */}
        <div>
          {activeSection === 'overview' && (
            <>
              {/* Stats row */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
                {[
                  { v: data.mandate_count ?? '—', l: t('profile.totalMandates'), c: '#110E0A' },
                  { v: data.won_count ?? '—', l: t('profile.successfulBids'), c: GOLD },
                  { v: data.order_count ?? data.won_count ?? '—', l: t('profile.completed'), c: '#2A6645' },
                  { v: totalAmount > 10000 ? `¥${(totalAmount/10000).toFixed(1)}${t('profile.tenThousand')}` : `¥${totalAmount.toLocaleString()}`, l: t('profile.totalTransaction'), c: GOLD },
                ].map(s => (
                  <div key={s.l} style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, padding: '18px 16px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 24, color: s.c, fontWeight: 600, lineHeight: 1, marginBottom: 6 }}>{s.v}</div>
                    <div style={{ fontSize: 11, color: MUTED_LIGHT }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Member tier */}
              <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, overflow: 'hidden', marginBottom: 20 }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BD, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 14, fontWeight: 600, color: '#110E0A' }}>{t('profile.memberBenefits')}</div>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 10, padding: '2px 8px', borderRadius: 3, background: 'rgba(184,137,42,0.1)', color: GOLD, border: '1px solid rgba(184,137,42,0.2)' }}>
                    {tierCfg.label}
                  </span>
                </div>
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: MUTED, marginBottom: 6 }}>
                    <span>{t('profile.upgradeTo')} {tierCfg.next}</span>
                    <span style={{ fontFamily: 'var(--mono)', color: GOLD }}>{progress}%</span>
                  </div>
                  <div style={{ height: 6, background: '#F2EFE9', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(90deg, ${GOLD}, #D4A843)`, borderRadius: 3, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ fontSize: 11, color: MUTED_LIGHT, marginTop: 6 }}>
                    {t('profile.spendMoreToUpgrade', { amount: (tierCfg.target - totalAmount > 0 ? tierCfg.target - totalAmount : 0).toLocaleString() })}
                  </div>
                </div>
              </div>

              {/* Quick links */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { icon: '💴', label: t('deposit.recharge'), onClick: () => navigate('/deposit') },
                  { icon: '📋', label: t('mandates.title'), onClick: () => navigate('/mandates') },
                  { icon: '⚙', label: t('settings.title'), onClick: () => navigate('/settings') },
                ].map(item => (
                  <button key={item.label} onClick={item.onClick} style={{
                    padding: '9px 18px', background: '#fff', border: '1px solid ' + BD,
                    borderRadius: 3, cursor: 'pointer', fontSize: 12, color: MUTED,
                    display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
                  >
                    {item.icon} {item.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {activeSection === 'kyc' && (
            <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BD, background: 'linear-gradient(135deg,#faf8f3,#f3ede3)' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: '#110E0A', letterSpacing: '0.1em' }}>{t('profile.kyc')}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, marginTop: 3 }}>IDENTITY VERIFICATION</div>
              </div>
              <div style={{ padding: 24 }}>
                {kycKey === 'verified' ? (
                  <div style={{ padding: '20px', background: 'rgba(42,102,69,0.06)', border: '1px solid rgba(42,102,69,0.2)', borderRadius: 3 }}>
                    <div style={{ fontSize: 14, color: '#2A6645', marginBottom: 6, fontWeight: 500 }}>✓ {t('profile.kycVerifiedTitle')}</div>
                    <div style={{ fontSize: 12, color: MUTED_LIGHT }}>{t('profile.kycVerifiedDesc')}</div>
                    {data.real_name && <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{t('profile.name')}：{data.real_name}</div>}
                    {data.id_number && <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{t('profile.idNumber')}：{data.id_number.replace(/^(.{4}).*(.{4})$/, '$1****$2')}</div>}
                  </div>
                ) : kycKey === 'under_review' ? (
                  <div style={{ padding: '20px', background: 'rgba(140,90,10,0.06)', border: '1px solid rgba(140,90,10,0.2)', borderRadius: 3 }}>
                    <div style={{ fontSize: 14, color: '#8C5A0A', marginBottom: 6, fontWeight: 500 }}>⏳ {t('profile.kycPending')}</div>
                    <div style={{ fontSize: 12, color: MUTED_LIGHT }}>{t('profile.kycReviewTime')}</div>
                  </div>
                ) : (
                  <form onSubmit={handleKycSubmit} style={{ maxWidth: 520 }}>
                    {kycKey === 'rejected' && (
                      <div style={{ padding: '12px 14px', background: 'rgba(155,35,53,0.06)', border: '1px solid rgba(155,35,53,0.2)', borderRadius: 3, marginBottom: 16, fontSize: 12, color: '#9B2335' }}>
                        ⚠ {t('profile.kycRejectedMsg')}{data.kyc_reject_reason && `：${data.kyc_reject_reason}`}
                      </div>
                    )}
                    <div style={{ padding: '10px 12px', background: 'rgba(26,79,140,0.06)', border: '1px solid rgba(26,79,140,0.15)', borderRadius: 3, marginBottom: 20, fontSize: 12, color: '#1A4F8C' }}>
                      {t('profile.kycBenefitHint')}
                    </div>
                    {/* 姓名 + 证件号 */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: MUTED_LIGHT, marginBottom: 7 }}>{t('profile.name')}</label>
                      <input type="text" value={realName} onChange={e => setRealName(e.target.value)} placeholder={t('profile.realNamePlaceholder')} style={inputStyle(focusedField === 'rn')} onFocus={() => setFocusedField('rn')} onBlur={() => setFocusedField(null)} />
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: MUTED_LIGHT, marginBottom: 7 }}>{t('profile.idNumber')}</label>
                      <input type="text" value={idNumber} onChange={e => setIdNumber(e.target.value)} placeholder={t('profile.idNumberPlaceholder')} maxLength={18} style={inputStyle(focusedField === 'id')} onFocus={() => setFocusedField('id')} onBlur={() => setFocusedField(null)} />
                    </div>
                    {/* 证件照片 */}
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: MUTED_LIGHT, marginBottom: 12 }}>{t('profile.idPhotosRequired')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                      {[
                        { key: 'id_front', label: t('profile.idFrontLabel'), hint: t('profile.idFrontHint') },
                        { key: 'id_back',  label: t('profile.idBackLabel'), hint: t('profile.idBackHint') },
                        { key: 'selfie',   label: t('profile.selfieLabel'), hint: t('profile.selfieHint') },
                      ].map(({ key, label, hint }) => {
                        const url = kycPhotos[key];
                        const uploading = kycUploading[key];
                        return (
                          <label key={key} style={{ cursor: 'pointer', display: 'block' }}>
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                              onChange={e => handlePhotoUpload(key, e.target.files[0])} />
                            <div style={{
                              border: `1px dashed ${url ? '#2A6645' : BD}`,
                              borderRadius: 4, background: url ? 'rgba(42,102,69,0.04)' : BG,
                              height: 100, display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center', gap: 4,
                              position: 'relative', overflow: 'hidden',
                            }}>
                              {url ? (
                                <img src={url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : uploading ? (
                                <div style={{ fontSize: 11, color: MUTED_LIGHT }}>{t('profile.uploading')}</div>
                              ) : (
                                <>
                                  <div style={{ fontSize: 20 }}>📷</div>
                                  <div style={{ fontSize: 11, color: MUTED, fontWeight: 500 }}>{label}</div>
                                  <div style={{ fontSize: 10, color: MUTED_LIGHT }}>{hint}</div>
                                </>
                              )}
                              {url && (
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(42,102,69,0.8)', color: '#fff', fontSize: 10, padding: '3px', textAlign: 'center' }}>
                                  ✓ {t('profile.uploadedClickReupload')}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <button type="submit" disabled={kycSubmitting || Object.values(kycUploading).some(v => v)}
                      style={{ padding: '11px 24px', background: kycSubmitting ? '#ccc' : GOLD, border: 'none', borderRadius: 3, cursor: kycSubmitting ? 'not-allowed' : 'pointer', color: '#fff', fontFamily: 'var(--serif)', fontSize: 14, letterSpacing: 2 }}>
                      {kycSubmitting ? t('common.loading') : t('profile.submitKyc')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          )}

          {activeSection === 'password' && (
            <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BD, background: 'linear-gradient(135deg,#faf8f3,#f3ede3)' }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 600, color: '#110E0A', letterSpacing: '0.1em' }}>{t('settings.changePassword')}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, marginTop: 3 }}>CHANGE PASSWORD</div>
              </div>
              <div style={{ padding: 24 }}>
                {pwdMsg && (
                  <div style={{ padding: '10px 14px', borderRadius: 3, fontSize: 12, marginBottom: 16, background: pwdMsg.type === 'err' ? 'rgba(155,35,53,0.06)' : 'rgba(42,102,69,0.06)', border: '1px solid ' + (pwdMsg.type === 'err' ? 'rgba(155,35,53,0.2)' : 'rgba(42,102,69,0.2)'), color: pwdMsg.type === 'err' ? '#9B2335' : '#2A6645' }}>
                    {pwdMsg.text}
                  </div>
                )}
                <form onSubmit={handlePasswordChange} style={{ maxWidth: 480 }}>
                  {[
                    { label: t('settings.currentPassword'), value: currentPwd, onChange: setCurrentPwd, field: 'cpwd', placeholder: t('auth.passwordPlaceholder') },
                    { label: t('settings.newPassword'), value: newPwd, onChange: setNewPwd, field: 'npwd', placeholder: t('auth.newPasswordPlaceholder') },
                    { label: t('settings.confirmNewPassword'), value: confirmPwd, onChange: setConfirmPwd, field: 'cfpwd', placeholder: t('auth.confirmPasswordPlaceholder') },
                  ].map(({ label, value, onChange, field, placeholder }) => (
                    <div key={field} style={{ marginBottom: 16, position: 'relative' }}>
                      <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: MUTED_LIGHT, marginBottom: 7 }}>{label}</label>
                      <input type={showPwd ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...inputStyle(focusedField === field), paddingRight: 40 }} onFocus={() => setFocusedField(field)} onBlur={() => {
                        setFocusedField(null);
                        // 确认密码失焦时实时校验
                        if (field === 'cfpwd' && value && newPwd && value !== newPwd) {
                          setPwdMsg({ type: 'err', text: t('profile.passwordMismatch') });
                        } else if (field === 'cfpwd' && value && newPwd && value === newPwd && pwdMsg?.text === t('profile.passwordMismatch')) {
                          setPwdMsg(null);
                        }
                      }} />
                      {field === 'cpwd' && (
                        <span onClick={() => setShowPwd(!showPwd)} style={{ position: 'absolute', right: 13, bottom: 12, cursor: 'pointer', fontSize: 13, color: MUTED_LIGHT }}>
                          {showPwd ? '🙈' : '👁'}
                        </span>
                      )}
                    </div>
                  ))}
                  <button type="submit" disabled={pwdSubmitting} style={{ padding: '11px 24px', background: pwdSubmitting ? '#ccc' : GOLD, border: 'none', borderRadius: 3, cursor: pwdSubmitting ? 'not-allowed' : 'pointer', color: '#fff', fontFamily: 'var(--serif)', fontSize: 14, letterSpacing: 2 }}>
                    {pwdSubmitting ? t('common.loading') : t('settings.changePassword')}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
